import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  BarChart3,
  TrendingUp,
  Shield,
  Users,
  FileText,
  Zap,
  Check,
  ArrowRight,
  ShieldCheck,
  Package,
  Microscope,
  AlertTriangle,
  Clock,
  Settings,
  CheckCircle,
  LineChart,
} from 'lucide-react';

export default function HomePage() {
  const navigate = useNavigate();

  const features = [
    {
      icon: <BarChart3 className="w-6 h-6" />,
      title: 'Real-Time Dashboard',
      description: 'Monitor all cannabis operations with comprehensive KPIs including compliance score, inventory value, and license status at a glance.',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
    },
    {
      icon: <ShieldCheck className="w-6 h-6" />,
      title: 'License Management',
      description: 'Track all operator licenses with expiration alerts, automatic renewal reminders, and complete document management system.',
      color: 'text-green-600',
      bgColor: 'bg-green-50',
    },
    {
      icon: <Package className="w-6 h-6" />,
      title: 'Seed-to-Sale Inventory',
      description: 'Complete inventory tracking from cultivation to sale with batch management, stock alerts, and automated COA integration.',
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
    },
    {
      icon: <Microscope className="w-6 h-6" />,
      title: 'Lab & COA Management',
      description: 'Track test results, certificates of analysis, and safety compliance for microbials, pesticides, and heavy metals.',
      color: 'text-pink-600',
      bgColor: 'bg-pink-50',
    },
    {
      icon: <FileText className="w-6 h-6" />,
      title: 'Compliance Reports',
      description: 'Generate and submit state-required reports with audit trails, submission tracking, and automated deadline management.',
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: 'Advanced Analytics',
      description: 'Year-over-year comparisons, state performance metrics, inventory turnover rates, and comprehensive business intelligence.',
      color: 'text-indigo-600',
      bgColor: 'bg-indigo-50',
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: 'Operator Management',
      description: 'Search, filter, and manage cannabis operators across multiple states with advanced sorting and export capabilities.',
      color: 'text-cyan-600',
      bgColor: 'bg-cyan-50',
    },
    {
      icon: <AlertTriangle className="w-6 h-6" />,
      title: 'Automated Alerts',
      description: 'Real-time notifications for license expirations, low inventory, missing COAs, and compliance report deadlines.',
      color: 'text-red-600',
      bgColor: 'bg-red-50',
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: 'Compliance Health Score',
      description: 'Multi-dimensional compliance scoring across license validity, inventory accuracy, reporting timeliness, and documentation.',
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50',
    },
  ];

  const complianceFeatures = [
    {
      title: 'State Reporting',
      items: ['Monthly compliance reports', 'Inventory reconciliation', 'Waste disposal logs', 'Financial reporting'],
    },
    {
      title: 'License Tracking',
      items: ['Expiration monitoring', 'Renewal reminders', 'Document storage', 'Multi-state support'],
    },
    {
      title: 'Quality Control',
      items: ['COA management', 'Lab result tracking', 'Safety compliance', 'Batch testing records'],
    },
  ];

  const pricingPlans = [
    {
      name: 'Starter',
      price: '$35',
      description: 'Basic plan for small operators',
      features: [
        '250 Operators',
        '1 State',
        'Compliance Reports',
        'License Management',
        'Basic Analytics',
        'Email Support',
      ],
      popular: false,
    },
    {
      name: 'Growth',
      price: '$105',
      description: 'For growing multi-state operations',
      features: [
        '750 Operators',
        '3 States',
        'Compliance Reports',
        'Excel Export',
        'Multi-State Access',
        'Multiple Users',
        'Audit Trail',
        'Priority Support',
      ],
      popular: true,
    },
    {
      name: 'Enterprise',
      price: '$205',
      description: 'Enterprise plan with all features',
      features: [
        '1250 Operators',
        '50 States',
        'All Compliance Reports',
        'Advanced Excel Export',
        'Full Multi-State Access',
        'Unlimited Users',
        'Complete Audit Trail',
        'AI Insights',
        '24/7 Dedicated Support',
      ],
      popular: false,
    },
  ];

  const stats = [
    { value: '500+', label: 'Cannabis Operators' },
    { value: '15', label: 'States Supported' },
    { value: '94.5%', label: 'Avg Compliance Score' },
    { value: '24/7', label: 'Real-Time Monitoring' },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navbar isLanding={true} />

      {/* Hero Section */}
      <section className="gradient-hero py-20 sm:py-32">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="mb-6 animate-fade-in" variant="secondary">
              <CheckCircle className="w-3 h-3 mr-1" />
              Trusted by 500+ Cannabis Companies
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold text-foreground mb-6 animate-fade-in">
              Cannabis Compliance Platform for{' '}
              <span className="text-primary">Modern Operators</span>
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground mb-8 max-w-2xl mx-auto animate-fade-in">
              Comprehensive cannabis analytics and compliance platform. Manage licenses, track inventory from seed-to-sale, ensure lab compliance, and generate state reports with confidence.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in">
              <Button size="lg" onClick={() => navigate('/register')} className="shadow-glow">
                Start Free Trial <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
              <Button size="lg" variant="outline" onClick={() => navigate('/login')}>
                View Demo
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-muted/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-3xl sm:text-4xl font-bold text-primary mb-2">{stat.value}</div>
                <div className="text-sm text-muted-foreground">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 sm:py-32">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="mb-4" variant="secondary">
              <Zap className="w-3 h-3 mr-1" />
              Comprehensive Features
            </Badge>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-foreground mb-4">
              Everything You Need for Cannabis Compliance
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              From license management to lab testing, inventory tracking to state reporting - all in one powerful platform.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card
                key={index}
                className="hover:shadow-xl transition-all duration-300 animate-fade-in border-0 shadow-md"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <CardHeader>
                  <div className={`w-12 h-12 rounded-lg ${feature.bgColor} flex items-center justify-center mb-4 ${feature.color}`}>
                    {feature.icon}
                  </div>
                  <CardTitle className="text-xl">{feature.title}</CardTitle>
                  <CardDescription className="text-sm leading-relaxed">{feature.description}</CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Compliance Features Detail */}
      <section className="py-20 sm:py-32 bg-muted/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-display font-bold text-foreground mb-4">
                Built for Cannabis Compliance
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Industry-specific features designed to keep you compliant with state regulations
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {complianceFeatures.map((section, index) => (
                <Card key={index} className="shadow-lg">
                  <CardHeader>
                    <CardTitle className="text-xl flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-primary" />
                      {section.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3">
                      {section.items.map((item, idx) => (
                        <li key={idx} className="flex items-start">
                          <Check className="w-5 h-5 text-success mr-2 flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-muted-foreground">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 sm:py-32">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="mb-4" variant="secondary">
              <LineChart className="w-3 h-3 mr-1" />
              Flexible Pricing
            </Badge>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-foreground mb-4">
              Choose Your Plan
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Scalable pricing for cannabis operations of all sizes. Start small, grow as you expand.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {pricingPlans.map((plan, index) => (
              <Card
                key={index}
                className={`relative flex flex-col hover:shadow-xl transition-shadow duration-300 ${
                  plan.popular ? 'border-primary shadow-glow scale-105' : ''
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <Badge className="shadow-lg">Most Popular</Badge>
                  </div>
                )}
                <CardHeader>
                  <CardTitle className="text-2xl">{plan.name}</CardTitle>
                  <CardDescription>{plan.description}</CardDescription>
                  <div className="mt-4">
                    <span className="text-4xl font-bold text-foreground">{plan.price}</span>
                    <span className="text-muted-foreground">/month</span>
                  </div>
                </CardHeader>
                <CardContent className="flex-grow">
                  <ul className="space-y-3 mb-6">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start">
                        <Check className="w-5 h-5 text-success mr-2 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-muted-foreground">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    className="w-full mt-auto"
                    variant={plan.popular ? 'default' : 'outline'}
                    onClick={() => navigate('/register')}
                  >
                    Get Started
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 sm:py-32 bg-muted/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-foreground mb-6">
              Ready to Simplify Your Cannabis Compliance?
            </h2>
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
              Join hundreds of cannabis operators already using GreenInteliX to streamline their compliance, manage inventory, and ensure regulatory adherence across multiple states.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" onClick={() => navigate('/register')} className="shadow-glow">
                Start Your Free Trial <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
              <Button size="lg" variant="outline" onClick={() => navigate('/login')}>
                Schedule a Demo
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
}
